import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { ContactStyles } from '../styles/ContactStyles';
import SendIcon from '@mui/icons-material/Send';
import { useMutation, useQuery } from '@apollo/client';
import { SEND_CONTACT_MESSAGE } from '../graphql/sendContactMessage';
import { GET_ME } from '../graphql/getMe';

const Contact = () => {
  const { data: me } = useQuery(GET_ME);
  const [sendContactMessage] = useMutation(SEND_CONTACT_MESSAGE);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendContactMessage({
        variables: {
          contactMessageInput: {
            name: formData.name,
            subject: formData.subject,
            message: formData.message,
          },
        },
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });

      setShowSuccess(true);
    } catch (error) {
      console.error('Error sending contact message:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={ContactStyles.root}>
      {/* Header Section */}
      <Box sx={ContactStyles.headerSection}>
        <Typography sx={ContactStyles.title}>Get in Touch</Typography>
        <Typography sx={ContactStyles.subtitle}>
          Have questions about Prep Qi? We're here to help you succeed on your
          board exam journey. Check out our{' '}
          <a href="/faq" style={{ color: '#1976d2', textDecoration: 'none' }}>
            FAQ & Return Policy
          </a>{' '}
          for quick answers to common questions.
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={ContactStyles.mainContent}>
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12}>
            <Paper sx={ContactStyles.formPaper}>
              <Typography sx={ContactStyles.formTitle}>
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      sx={ContactStyles.textField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={me?.getMe?.email || ''}
                      disabled
                      sx={ContactStyles.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      sx={ContactStyles.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      sx={ContactStyles.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      startIcon={<SendIcon />}
                      sx={ContactStyles.submitButton}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Success/Error Messages */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Message sent successfully! A copy has been sent to your email address.
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          Failed to send message. Please try again or contact us directly.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
